import _compact from 'lodash/compact';
import { LeftTooltip } from 'ui/tooltip/Tooltip';
import { ActionMenu } from 'ui/actionMenu/ActionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Archive24 } from 'ui/icons/aurora/24px/Archive24';
import { ConnectedReferences24 } from 'ui/icons/aurora/24px/ConnectedReferences24';
import { DatabaseAccept24 } from 'ui/icons/aurora/24px/DatabaseAccept24';
import { DatabaseBlock24 } from 'ui/icons/aurora/24px/DatabaseBlock24';
import { DatabaseRetain24 } from 'ui/icons/aurora/24px/DatabaseRetain24';
import { Overflow16 } from 'ui/icons/aurora/16px/Overflow16';
import { useDrawersContext, useModalsContext } from '../contexts';
import { INGESTION_POLICIES } from '../types';

export const TEXT = {
  RETENTION_PERIOD: {
    ID: 'update-retention-period-action',
    LABEL: 'Update retention period',
  },
  REFERENCES: {
    ID: 'show-references-action',
    LABEL: 'View references',
  },
  DISCARD: {
    ID: 'discard-event-action',
    DESCRIPTION: 'Show this event in the app, but do not store new instances of it',
    LABEL: 'Discard',
  },
  DEACTIVATE: {
    ID: 'deactivate-event-action',
    DESCRIPTION: 'Hide this event in the app and do not store new instances of it',
    LABEL: 'Deactivate',
    TOOLTIP: 'Events with active references cannot be deactivated',
  },
  SAVE: {
    ID: 'save-event-action',
    DESCRIPTION: 'Show this event in the app and store new instances of it',
    LABEL: 'Save',
  },
};

type Props = {
  eventName: string;
  hasReferences: boolean;
  ingestionPolicyFilter: INGESTION_POLICIES;
  isEnabled?: boolean;
};

export const CustomEventIndexOverflowMenu = ({
  eventName,
  hasReferences,
  ingestionPolicyFilter,
  isEnabled = false,
}: Props) => {
  const { showSaveModal, showDiscardModal, showDeactivateModal, showRetentionPeriodModal } =
    useModalsContext();
  const { showDrawer } = useDrawersContext();

  const retentionPeriodOption = {
    as: AllowedActionMenuElements.Button,
    content: (
      <>
        <DatabaseRetain24 />
        <span>{TEXT.RETENTION_PERIOD.LABEL}</span>
      </>
    ),
    id: TEXT.RETENTION_PERIOD.ID,
    onClick: () => showRetentionPeriodModal([eventName]),
  };

  const shouldShowSave = isEnabled && ingestionPolicyFilter !== INGESTION_POLICIES.SAVED;
  const shouldShowDiscard = isEnabled && ingestionPolicyFilter !== INGESTION_POLICIES.DISCARDED;
  const shouldShowDeactivate =
    isEnabled && ingestionPolicyFilter !== INGESTION_POLICIES.DEACTIVATED;

  const overflowOptions: ActionMenuOption[] = _compact([
    hasReferences && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <ConnectedReferences24 />
          <span>{TEXT.REFERENCES.LABEL}</span>
        </>
      ),
      id: TEXT.REFERENCES.ID,
      onClick: () => showDrawer(eventName),
    },
    isEnabled ? { ...retentionPeriodOption, appendDivider: true } : { ...retentionPeriodOption },
    shouldShowSave && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <DatabaseAccept24 />
          <span>{TEXT.SAVE.LABEL}</span>
        </>
      ),
      description: TEXT.SAVE.DESCRIPTION,
      id: TEXT.SAVE.ID,
      onClick: () => showSaveModal(eventName),
    },
    shouldShowDiscard && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <DatabaseBlock24 />
          <span>{TEXT.DISCARD.LABEL}</span>
        </>
      ),
      description: TEXT.DISCARD.DESCRIPTION,
      id: TEXT.DISCARD.ID,
      onClick: () => showDiscardModal(eventName),
    },
    shouldShowDeactivate && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Archive24 />
          <span>{TEXT.DEACTIVATE.LABEL}</span>
        </>
      ),
      description: TEXT.DEACTIVATE.DESCRIPTION,
      id: TEXT.DEACTIVATE.ID,
      onClick: () => showDeactivateModal(eventName),
      isDisabled: hasReferences,
      Wrapper: ({ children }) => (
        <LeftTooltip isEnabled={hasReferences} tooltipContent={TEXT.DEACTIVATE.TOOLTIP}>
          {children}
        </LeftTooltip>
      ),
    },
  ]);

  return (
    <ActionMenu align={MENU_ALIGNMENTS.RIGHT} options={overflowOptions}>
      <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
        <Overflow16 />
      </IconButton>
    </ActionMenu>
  );
};
